import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IRowNode } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai/index';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { DocumentFieldAPIModel } from '~/api/def/endpoints/document/documentFieldEndpoint';
import { endpointInvestorGET } from '~/api/def/endpoints/investors';
import { documentMetaAtom } from '~/atoms/document/documentOverviewAtoms';

import { SelectButton } from 'primereact/selectbutton';
import { Toast } from 'primereact/toast';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  DocumentCapTableResponse,
  endpointDocumentCapTableDelete,
  endpointDocumentCapTableGET,
  endpointDocumentCapTablePATCH,
} from '~/api/def/endpoints/document/documentCapTableEndpoint';
import { pcEvaluationEventPOST } from '~/api/def/endpoints/pcValuationEventEndpoint';
import { toastAtom } from '~/atoms/toastAtom';
import { agTheme } from '~/components/ui/tables/ag/agTheme';
import { colTypesWidths } from '~/components/ui/tables/ag/columnTypes';
import { APIFieldToPDFField } from '~/lib/pdfViewer/api2pdf';
import { DocumentPDFField } from '~/lib/pdfViewer/types';
import {
  colDefsDocumentOverviewCapTable,
  DocumentOverviewCapTableRow,
} from './colDefsDocumentOverviewCapTable';
import {
  EditFormFields,
  shareClassValueToLabel,
  ValidationSchema,
  validationSchema,
} from './EditFormFields';

export type DocumentOverviewCapTableProps = {
  documentId: string;
  portfolioCompanyId: string;
  scrollToHighlight: (fieldId: string) => void;
  setPdfFields: (fields: DocumentPDFField[]) => void;
};

type SharesFilter = 'pre-completion' | 'investment-round';

const availableSharesFilters: { name: string; value: SharesFilter }[] = [
  { name: 'Existing Shares', value: 'pre-completion' },
  { name: 'New Shares', value: 'investment-round' },
];

export const DocumentOverviewCapTable = ({
  documentId,
  portfolioCompanyId,
  scrollToHighlight,
  setPdfFields,
}: DocumentOverviewCapTableProps) => {
  const [shareholderNameSearch, setShareholderNameSearch] =
    useState<string>('');
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const gridRef = useRef<AgGridReact<DocumentOverviewCapTableRow>>(null);
  const [editRowDialogVisible, setEditRowDialogVisible] = useState(false);
  const [sharesFilter, setSharesFilter] = useState<SharesFilter[]>([]);
  const [selectedRow, setSelectedRow] = useState<
    DocumentCapTableResponse | undefined
  >(undefined);
  const [successDialogVisible, setSuccessDialogVisible] = useState(false);
  const [documentMeta] = useAtom(documentMetaAtom);

  const {
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ValidationSchema>({
    resolver: async (values, context, options) => {
      return zodResolver(validationSchema)(values, context, options);
    },
    defaultValues: {
      shareholder: '',
      shareClass: '',
      shareCount: 0,
      investmentAmount: 0,
      pricePerShare: 0,
      investmentDate: new Date(),
    },
  });

  const {
    data: capTableData = {
      entries: [],
      aggregates: { totalSharesCount: '0' },
      fields: [],
    },
    isLoading: isCapTableLoading,
  } = useQuery({
    queryKey: ['capTable', documentId, sharesFilter],
    queryFn: () =>
      endpointDocumentCapTableGET(documentId, sharesFilter).then(
        (res) => res?.data,
      ),
    enabled: !!documentId,
  });

  const { data: investorsData } = useQuery({
    queryKey: ['investors'],
    queryFn: () => endpointInvestorGET(),
  });

  console.log('Investors data:', investorsData);

  useMemo(() => {
    const pdfFields = capTableData.fields.map((field: DocumentFieldAPIModel) =>
      APIFieldToPDFField(field),
    );
    if (pdfFields.length) {
      setPdfFields(pdfFields);
    }
  }, [capTableData.fields, setPdfFields]);

  const queryClient = useQueryClient();

  const refreshCapTable = () => {
    queryClient.invalidateQueries({ queryKey: ['capTable', documentId] });
  };

  const updateCapTableMutation = useMutation({
    mutationFn: ({
      docId,
      rowId,
      fields,
    }: {
      docId: string;
      rowId: string;
      fields: { id: string; value: string }[];
    }) =>
      endpointDocumentCapTablePATCH(docId, rowId, { updatedFields: fields }),
    onSuccess: (res) => {
      if (res?.response !== 200) {
        toastCurrent?.show({
          severity: 'error',
          detail: 'Something went wrong.',
        });
        return;
      }

      refreshCapTable();
      toastCurrent?.show({
        severity: 'success',
        detail: 'Row updated successfully.',
      });
    },
    onError: () => {
      toastCurrent?.show({
        severity: 'error',
        detail: 'Something went wrong.',
      });
    },
    onSettled: () => {
      setEditRowDialogVisible(false);
    },
  });

  const deleteCapTableMutation = useMutation({
    mutationFn: ({
      docId,
      capTableRowId,
    }: {
      docId: string;
      capTableRowId: string;
    }) => endpointDocumentCapTableDelete(docId, capTableRowId),
    onSuccess: (res) => {
      if (res?.response !== 200) {
        return toastCurrent?.show({
          severity: 'error',
          detail: 'Something went wrong.',
        });
      }

      refreshCapTable();
      toastCurrent?.show({
        severity: 'success',
        detail: 'Row deleted successfully.',
      });
    },
    onError: () => {
      toastCurrent?.show({
        severity: 'error',
        detail: 'Something went wrong.',
      });
    },
  });

  const confirmDeleteDialog = (capTableRowId: string) => {
    confirmDialog({
      message: 'Do you want to delete this record?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      accept: () => handleDelete(capTableRowId),
    });
  };

  const createValuationEventMutation = useMutation({
    mutationFn: () =>
      pcEvaluationEventPOST({
        portfolioCompanyId,
        extractedFieldIds: capTableData.entries.map(
          (capTable) => capTable.capTableRowId,
        ),
        documentId,
      }),
    onSuccess: (res) => {
      if (res?.response !== 200) {
        return toastCurrent?.show({
          severity: 'error',
          summary: 'Something went wrong',
        });
      }
      refreshCapTable();
      setSuccessDialogVisible(true);
      toastCurrent?.show({
        severity: 'success',
        summary: 'Evaluation event created successfully',
      });
    },
    onError: () => {
      toastCurrent?.show({
        severity: 'error',
        summary: 'Something went wrong',
      });
    },
  });

  const onSubmit = useCallback(
    (data: ValidationSchema) => {
      const changedFields: Array<{
        field: keyof ValidationSchema;
        value: string;
        id?: string;
      }> = [];

      if (selectedRow) {
        Object.keys(data).forEach((key) => {
          if (
            data[key as keyof ValidationSchema] !==
            selectedRow[key as keyof DocumentCapTableResponse]
          ) {
            const idKey = `${key}Id` as keyof ValidationSchema;
            const idValue = selectedRow[
              idKey as keyof DocumentCapTableResponse
            ] as string | undefined;
            changedFields.push({
              field: key as keyof ValidationSchema,
              value: String(data[key as keyof ValidationSchema]),
              ...(idValue ? { id: idValue } : {}),
            });
          }
        });
      }

      if (changedFields.length && documentId && selectedRow?.capTableRowId) {
        const validChangedFields = changedFields.filter(
          (field) => field.id !== undefined,
        ) as { id: string; value: string }[];

        updateCapTableMutation.mutate({
          docId: documentId,
          rowId: selectedRow.capTableRowId,
          fields: validChangedFields,
        });
      }
    },
    [documentId, selectedRow, updateCapTableMutation],
  );

  const handleDelete = (capTableRowId: string) => {
    if (documentId) {
      deleteCapTableMutation.mutate({
        docId: documentId,
        capTableRowId,
      });
    }
  };

  const externalFilterChanged = useCallback((newValue: string) => {
    setShareholderNameSearch(newValue);
    gridRef.current?.api.onFilterChanged();
  }, []);

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<DocumentOverviewCapTableRow>) => {
      return !!node.data?.shareholder
        .toLocaleLowerCase()
        .includes(shareholderNameSearch?.toLowerCase());
    },
    [shareholderNameSearch],
  );

  const getPricePerShare = (row: DocumentCapTableResponse) => {
    if (row.pricePerShare !== '-') return Number(row.pricePerShare);
    if (row.shareCount && row.investmentAmount) {
      return +(Number(row.investmentAmount) / Number(row.shareCount)).toFixed(
        2,
      );
    }
    return 0;
  };

  const handleEdit = (
    capTableRowId: string,
    entries: DocumentCapTableResponse[],
  ) => {
    const row = entries.find((row) => row.capTableRowId === capTableRowId);

    if (row) {
      setSelectedRow(row);
      setValue('capTableRowId', row.capTableRowId);
      setValue('shareholder', row.shareholder);
      setValue('shareholderId', row.shareholderId);
      setValue('shareClass', row.shareClass);
      setValue('shareClassId', row.shareClassId);
      setValue('shareCount', Number(row.shareCount));
      setValue('shareCountId', row.shareCountId);
      setValue('investmentCurrency', row.investmentCurrency);
      setValue('investmentCurrencyId', row.investmentCurrencyId);
      setValue('investmentAmount', Number(row.investmentAmount));
      setValue('investmentAmountId', row.investmentAmountId);
      setValue('pricePerShare', getPricePerShare(row));
      setValue('pricePerShareId', row.pricePerShareId);
      setValue(
        'investmentDate',
        row.investmentDate !== '-' ? new Date(row.investmentDate) : new Date(),
      );
      setValue('investmentDateId', row.investmentDateId);
    }

    setEditRowDialogVisible(true);
  };

  const handleDeleteCallback = useCallback((capTableRowId: string) => {
    return () => confirmDeleteDialog(capTableRowId);
  }, []);

  const handleEditCallback = useCallback(
    (capTableRowId: string, entries: DocumentCapTableResponse[]) => {
      return () => handleEdit(capTableRowId, entries);
    },
    [],
  );

  const memoizedRowData = useMemo(
    () =>
      capTableData.entries.map((row) => ({
        ...row,
        shareCount: Number(row.shareCount).toLocaleString('en-GB'),
        investmentAmount: Number(row.investmentAmount).toLocaleString('en-GB'),
        pricePerShare: getPricePerShare(row),
        investmentDate:
          row.investmentDate && row.investmentDate !== '-'
            ? new Date(row.investmentDate).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })
            : new Date().toLocaleDateString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }),
        shareClass: shareClassValueToLabel[row.shareClass] || row.shareClass,
        handleDelete: handleDeleteCallback(row.capTableRowId),
        handleEdit: handleEditCallback(row.capTableRowId, capTableData.entries),
        scrollToHighlight: scrollToHighlight,
        isFundInvestment: investorsData?.data?.some(
          (investor) =>
            investor.name === row.shareholder &&
            investor.type === 'fund' &&
            investor.investorFundId === documentMeta?.fundId,
        ),
      })),
    [
      capTableData.entries,
      handleDeleteCallback,
      handleEditCallback,
      scrollToHighlight,
      investorsData?.data,
      documentMeta?.fundId,
    ],
  );

  const handleGridSizeChanged = useCallback(() => {
    gridRef.current?.api.sizeColumnsToFit();
  }, []);

  return (
    <div
      className="flex flex-column gap-3 h-full"
      style={{ marginLeft: '1rem' }}
    >
      <div className="flex flex-column gap-2">
        <div className="flex justify-content-between">
          <div className="flex flex-row gap-2 align-items-center">
            <InputText
              value={shareholderNameSearch}
              className="search-input w-20rem"
              style={{
                backgroundColor: 'var(--bq-color-gray-lighter)',
              }}
              onChange={(e) => externalFilterChanged(e.target.value)}
              placeholder="Search by Shareholder name"
            />
            <div
              className="border-round shadow-none p-2"
              style={{
                border: '1px solid lightgrey',
              }}
            >
              Fully Diluted Shares:&nbsp;
              {Number(capTableData.aggregates.totalSharesCount).toLocaleString(
                'en-GB',
              )}
            </div>
            <SelectButton
              type="primary"
              value={sharesFilter}
              onChange={(e) => setSharesFilter(e.value)}
              optionLabel="name"
              options={availableSharesFilters}
              multiple
            />
          </div>

          <Button
            label="Mark as Valuation event"
            size="small"
            icon="pi pi-calendar"
            tooltip="Save"
            tooltipOptions={{ position: 'top' }}
            loading={createValuationEventMutation.isPending}
            onClick={() => {
              createValuationEventMutation.mutate();
            }}
          />
        </div>
      </div>
      <div className="h-full">
        <AgGridReact
          rowData={memoizedRowData}
          ref={gridRef}
          loading={isCapTableLoading}
          isExternalFilterPresent={() => shareholderNameSearch !== ''}
          doesExternalFilterPass={doesExternalFilterPass}
          columnDefs={colDefsDocumentOverviewCapTable}
          columnTypes={colTypesWidths}
          tooltipShowDelay={0}
          theme={agTheme}
          suppressCellFocus
          onGridSizeChanged={handleGridSizeChanged}
          autoSizeStrategy={{
            type: 'fitGridWidth',
            defaultMinWidth: 100,
          }}
        />
      </div>
      <Dialog
        header="Edit Values"
        visible={editRowDialogVisible}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setEditRowDialogVisible(false);
                reset();
              }}
              className="p-button-text"
            />
            <Button
              type="submit"
              form="editCapTableForm"
              label="Update"
              icon="pi pi-check"
              autoFocus
            />
          </div>
        }
        onHide={() => {
          if (!editRowDialogVisible) return;
          setEditRowDialogVisible(false);
          reset();
        }}
        style={{ width: '50rem' }}
      >
        <div className="flex flex-column gap-2">
          <EditFormFields
            control={{ ...control, setValue }}
            errors={errors}
            investors={investorsData?.data || []}
            onSubmit={onSubmit}
          />
        </div>
      </Dialog>

      <Dialog
        header="Success"
        visible={successDialogVisible}
        onHide={() => setSuccessDialogVisible(false)}
        footer={
          <Button
            label="OK"
            icon="pi pi-check"
            onClick={() => setSuccessDialogVisible(false)}
            autoFocus
          />
        }
        style={{ width: '30rem' }}
      >
        <div className="flex align-items-center gap-2">
          <i className="pi pi-check-circle text-green-500 text-xl"></i>
          <span>Valuation event has been created successfully.</span>
        </div>
      </Dialog>
    </div>
  );
};
